import { HelmetServerState } from 'react-helmet-async';
import { ScriptContent } from '../components/ConsentMode/ConsentMode';
import { Config } from '../config';

function jsValue(value: unknown): string {
	return JSON.stringify(value ?? null)
		.replace(/</g, '\\u003C')
		.replace(/>/g, '\\u003E')
		.replace(/&/g, '\\u0026')
		.replace(/\u2028/g, '\\u2028')
		.replace(/\u2029/g, '\\u2029');
}

function jsString(value: unknown): string {
	return jsValue(String(value ?? ''));
}

function htmlAttribute(value: unknown): string {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

function urlQueryValue(value: unknown): string {
	return encodeURIComponent(String(value ?? ''));
}

function inlineScriptErrorReporter(origin: string): string {
	return `try {
		console.error(_error);
		if (window.__kgCaptureInlineScriptError) {
			window.__kgCaptureInlineScriptError(_error, ${jsString(origin)});
		} else {
			window.__kgInlineScriptErrors = window.__kgInlineScriptErrors || [];
			window.__kgInlineScriptErrors.push({
				message: _error && _error.message ? String(_error.message) : String(_error),
				stack: _error && _error.stack ? String(_error.stack) : undefined,
				origin: ${jsString(origin)}
			});
		}
	} catch (_reportingError) {}`;
}

function getGoogleTagManager(config: Config) {
	const isProductionEnv = process.env.NODE_ENV === 'production';
	const analyticsId = jsString(config.analyticsId);
	const ga4Id = jsString(config.ga4Id);
	const googleAdsId = jsString(config.googleAdsId);
	const ga4IdQuery = urlQueryValue(config.ga4Id);
	return `
	<script async src="https://www.googletagmanager.com/gtag/js?id=${ga4IdQuery}"></script>
	<script>(function () { try {
		window.dataLayer = window.dataLayer || [];
		window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };
		window.gtag('js', new Date());
		${
			config.store === 'kgno'
				? `
		window.gtag("consent", "default", {
			ad_storage: "denied",
			ad_user_data: "denied",
			ad_personalization: "denied",
			analytics_storage: "denied",
			functionality_storage: "denied",
			personalization_storage: "denied",
			security_storage: "granted"
		});`
				: ''
		}
		window.gtag('config', ${analyticsId});
		window.gtag('config', ${ga4Id});
		window.gtag('config', ${googleAdsId});
		${config.store === 'kgdk' && isProductionEnv ? `window.gtag('config', 'AW-16455556905');` : ''}
	} catch (_error) { ${inlineScriptErrorReporter('google_tag_manager')} } })();</script>
	`;
}

function torkConversionLinker(config: Config) {
	if (config.store !== 'kgno') return '';
	return `
<script async src="https://www.googletagmanager.com/gtag/js?id=AW-712418991"></script>
<script>
	window.dataLayer = window.dataLayer || [];
	window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };

	window.gtag('config', 'AW-712418991', {
		conversion_linker: { enable: true }
	});
</script>
	`;
}

function torkFacebookTracking(config: Config) {
	if (config.store !== 'kgno') return '';
	const facebookId = '1130257161678352';
	return `<script>(function () { try {
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
	'https://connect.facebook.net/en_US/fbevents.js');
	fbq('init', ${jsString(facebookId)});
		} catch (_error) { ${inlineScriptErrorReporter('tork_facebook_tracking')} } })();</script>`;
}

export function hotjarTracking(config: Config): ScriptContent {
	if (!config.hotjarId) return undefined;

	return {
		js: `(function(h,o,t,j,a,r){
			h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
			h._hjSettings={hjid:${config.hotjarId},hjsv:6};
			a=o.getElementsByTagName('head')[0];
			r=o.createElement('script');r.async=1;
			r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
			a.appendChild(r);
		})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
	};
}

export function criteo(config: Config): ScriptContent {
	if (!config.criteoId) return undefined;

	return {
		script: {
			src: '//dynamic.criteo.com/js/ld/ld.js?a=14304',
			type: 'text/javascript',
			async: true,
		},
	};
}

export function facebookPixel(config: Config): ScriptContent {
	if (!config.facebookPixelId) return undefined;
	const facebookPixelId = jsString(config.facebookPixelId);
	const facebookPixelIdQuery = urlQueryValue(config.facebookPixelId);

	return {
		js: `!function(f,b,e,v,n,t,s)
	{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
	n.callMethod.apply(n,arguments):n.queue.push(arguments)};
	if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
	n.queue=[];t=b.createElement(e);t.async=!0;
	t.src=v;s=b.getElementsByTagName(e)[0];
	s.parentNode.insertBefore(t,s)}(window, document,'script',
	'https://connect.facebook.net/en_US/fbevents.js');
	fbq('init', ${facebookPixelId});
	fbq('track', 'PageView');`,
		noscript: `<img height="1" width="1" style="display:none"
	src="https://www.facebook.com/tr?id=${facebookPixelIdQuery}&ev=PageView&noscript=1"
	/>`,
	};
}

export function linkedinPixel(config: Config): ScriptContent {
	if (!config.linkedinPixelId) return undefined;
	const linkedinPixelId = jsString(config.linkedinPixelId);
	const linkedinPixelIdQuery = urlQueryValue(config.linkedinPixelId);

	return {
		js: `window._linkedin_partner_id = ${linkedinPixelId};
		window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
		window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
		(function(l) {
			if (!l) {
				window.lintrk = function(a, b) {
					window.lintrk.q.push([a, b]);
				};
				window.lintrk.q = [];
			}
			var s = document.getElementsByTagName("script")[0];
			var b = document.createElement("script");
			b.type = "text/javascript";
			b.async = true;
			b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
			s.parentNode.insertBefore(b, s);
		})(window.lintrk);`,
		noscript: `<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=${linkedinPixelIdQuery}&fmt=gif" />`,
	};
}

function snapEngage({ snapEngageId }: Config) {
	const safeSnapEngageId = jsString(snapEngageId);
	return `
	<script type="text/javascript">(function() { try {
		(function() {
		var se = document.createElement('script'); se.type = 'text/javascript'; se.async = true;
		se.src = 'https://storage.googleapis.com/code.snapengage.com/js/' + ${safeSnapEngageId} + '.js';
		var done = false;
		se.onload = se.onreadystatechange = function() {
			if (!done&&(!this.readyState||this.readyState==='loaded'||this.readyState==='complete')) {
			done = true;
			/* Place your SnapEngage JS API code below */
			/* SnapEngage.allowChatSound(true); Example JS API: Enable sounds for Visitors. */
			}
		};
		var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(se, s);
		})();
	} catch (_error) { ${inlineScriptErrorReporter('snapengage')} } })();</script>
	`;
}

const INHOUSE_CHAT_PROD_WIDGET_URL = 'https://chatwidget.kontorsgiganten.se/prod/chat-widget.inline.umd.js';

function inhouseChat({ inhouseChatApiUrl, inhouseChatAgentId, inhouseChatWidgetUrl }: Config) {
	const inhouseConfig = jsValue({
		apiUrl: inhouseChatApiUrl,
		agent_id: inhouseChatAgentId,
	});
	return `<script>
    (function() {
      var config = ${inhouseConfig};

      function tryInit() {
        if (typeof ChatWidget !== 'undefined' && ChatWidget.init) {
          ChatWidget.init(config);
        } else {
          setTimeout(tryInit, 100);
        }
      }

      var script = document.createElement('script');
      script.src = ${jsString(inhouseChatWidgetUrl || INHOUSE_CHAT_PROD_WIDGET_URL)};
      script.async = true;
      script.onload = tryInit;
      document.head.appendChild(script);
    })();
  </script>`;
}

function kundoChat(localization: unknown) {
	const startText =
		localization && (localization as any).kundo.startText
			? (localization as any).kundo.startText
			: 'Chatta med oss';

	return `<script>
  (function (w) {
    w.$kundo_chat = w.$kundo_chat || {};
    w.$kundo_chat.custom_texts = {
      START_TEXT: ${jsString(startText)},
    };
  }(this));
</script>
<script src="https://static-chat.kundo.se/chat-js/org/1580/widget.js" async defer></script>`;
}

export function bingAds({ bingAdsId }: Config): ScriptContent {
	const safeBingAdsId = jsString(bingAdsId);
	return {
		js: `(function (w, d, t, r, u) {
		var f, n, i;
		(w[u] = w[u] || []),
		(f = function () {
			var o = { ti: ${safeBingAdsId}, enableAutoSpaTracking: true };
			(o.q = w[u]), (w[u] = new UET(o)), w[u].push("pageLoad");
		}),
		(n = d.createElement(t)),
		(n.src = r),
		(n.async = 1),
		(n.onload = n.onreadystatechange =
			function () {
			var s = this.readyState;
			(s && s !== "loaded" && s !== "complete") ||
				(f(), (n.onload = n.onreadystatechange = null));
			}),
		(i = d.getElementsByTagName(t)[0]),
		i.parentNode.insertBefore(n, i);
	})(window, document, "script", "//bat.bing.com/bat.js", "uetq");`,
	};
}

export function awinTracking(config: Config): ScriptContent {
	if (config.store !== 'kgse') return undefined;

	return {
		script: {
			src: 'https://www.dwin1.com/8936.js',
			type: 'text/javascript',
			defer: true,
		},
	};
}

function gtagHead(config: Config) {
	if (config.gtagId === '') return '';
	const gtagId = jsString(config.gtagId);

	return `<script>(function(){ try { (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer',${gtagId}); } catch (_error) { ${inlineScriptErrorReporter('gtag_head')} } })();</script>`;
}

function timeBasedWidget(content: string, startHour: number, endHour: number) {
	if (!content) return '';
	const widgetId = `time-widget-${startHour}-${endHour}`;
	return `<div id="${widgetId}" style="display: none;">
		${content}
		<script>
			(function() {
				const now = new Date();
				const stockholmHour = parseInt(now.toLocaleString("en-US", {timeZone: "Europe/Stockholm", hour: "numeric", hour12: false}));
				const stockholmDay = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Stockholm"})).getDay(); // 0 = Sunday, 6 = Saturday
				let shouldShow = false;

				// Only show on weekdays (Monday-Friday)
				if (stockholmDay >= 1 && stockholmDay <= 5) {
					if (${startHour} <= ${endHour}) {
						// Normal range (e.g., 7-8)
						shouldShow = stockholmHour >= ${startHour} && stockholmHour < ${endHour};
					} else {
						// Range spans midnight (e.g., 22-02)
						shouldShow = stockholmHour >= ${startHour} || stockholmHour < ${endHour};
					}
				}

				if (shouldShow) {
					document.getElementById("${widgetId}").style.display = "block";
				}
			})();
		</script>
	</div>`;
}

function timeBasedWidgetExcept(content: string, excludeStartHour: number, excludeEndHour: number) {
	if (!content) return '';
	const widgetId = `time-widget-except-${excludeStartHour}-${excludeEndHour}`;
	return `<div id="${widgetId}" style="display: none;">
		${content}
		<script>
			(function() {
				const now = new Date();
				const stockholmHour = parseInt(now.toLocaleString("en-US", {timeZone: "Europe/Stockholm", hour: "numeric", hour12: false}));
				const stockholmDay = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Stockholm"})).getDay(); // 0 = Sunday, 6 = Saturday
				let shouldHide = false;

				// Only hide on weekdays (Monday-Friday)
				if (stockholmDay >= 1 && stockholmDay <= 5) {
					if (${excludeStartHour} <= ${excludeEndHour}) {
						// Normal range (e.g., 7-8)
						shouldHide = stockholmHour >= ${excludeStartHour} && stockholmHour < ${excludeEndHour};
					} else {
						// Range spans midnight (e.g., 22-02)
						shouldHide = stockholmHour >= ${excludeStartHour} || stockholmHour < ${excludeEndHour};
					}
				}
				// On weekends, shouldHide is always false (always show Kundo)

				function hideKundoElements() {
					const kundoSelectors = ['#kundo-chat', '#kundo-chat-widget', '#kundo-chat-app'];
					kundoSelectors.forEach(selector => {
						const element = document.querySelector(selector);
						if (element) {
							element.style.display = 'none';
						}
					});
				}

				function showKundoElements() {
					const kundoSelectors = ['#kundo-chat', '#kundo-chat-widget', '#kundo-chat-app'];
					kundoSelectors.forEach(selector => {
						const element = document.querySelector(selector);
						if (element) {
							element.style.display = 'block';
						}
					});
				}

				function handleKundoVisibility() {
					if (!shouldHide) {
						document.getElementById("${widgetId}").style.display = "block";
						showKundoElements();
					} else {
						hideKundoElements();
					}
				}

				// Initial check
				handleKundoVisibility();

				// Check again after Kundo script has time to load (handle async loading)
				setTimeout(handleKundoVisibility, 1000);
				setTimeout(handleKundoVisibility, 3000);
			})();
		</script>
	</div>`;
}

function gtagBody(config: Config) {
	if (config.gtagId === '') return '';
	const gtagIdQuery = urlQueryValue(config.gtagId);

	return `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${gtagIdQuery}"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`;
}

// how old a sign-in has to be before a visit counts as an existing customer's. a full day, so that
// the account an order creates does not turn that customer's same-day return to check on the order
// into existing-customer traffic
const EXISTING_CUSTOMER_AFTER_MS = 24 * 60 * 60 * 1000;

// sets the properties plausible reads off its script tag and adds to every pageview and event. it
// runs in the browser rather than here because these responses are cached by cloudflare and must
// stay identical for everyone; it is inline and placed after the deferred plausible tag so it runs
// before plausible does. html lowercases attribute names, so the property keys are all lowercase
export const plausibleCustomerTagScript = `(function () { try {
	var read = function (name) {
		var match = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
		if (!match) return '';
		try { return decodeURIComponent(match[1]); } catch (_) { return match[1]; }
	};
	var script = document.getElementById('plausible');
	if (!script) return;
	// the marker is only read under a current statistics consent, the same one that let it be
	// written: cookiebot's own cookie is shorter-lived than ours, and a lapsed or cleared consent
	// must not keep tagging the visitor as existing until the dialog has been answered again.
	// the cookie is cookiebot's: an url-encoded object, or -1 for no consent at all
	var consent = read('CookieConsent');
	var statisticsConsent = consent !== '-1' && /(?:^|[,{])\\s*statistics\\s*:\\s*true/i.test(consent);
	// parseInt would read a leading number out of a mangled value; only a whole timestamp counts
	var customerInfo = read('customerInfo');
	var signedInSince = /^[0-9]+$/.test(customerInfo) ? Number(customerInfo) : 0;
	var existing = statisticsConsent && signedInSince > 0 && Date.now() - signedInSince > ${EXISTING_CUSTOMER_AFTER_MS};
	script.setAttribute('event-customer', existing ? 'existing' : 'new');
	var customerType = read('customer-type');
	if (customerType) script.setAttribute('event-customer_type', customerType);
} catch (_error) { ${inlineScriptErrorReporter('plausible_customer_tag')} } })();`;

// the extensions in the script name must be in alphabetical order, that is the only spelling
// plausible publishes
function plausibleTracking(config: Config) {
	if (!config.plausibleTracking) return '';
	return `<script>window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }</script>
		<script id="plausible" defer data-domain="${htmlAttribute(config.baseUrl)}" src="https://plausible.io/js/script.pageview-props.revenue.tagged-events.js"></script>
		<script>${plausibleCustomerTagScript}</script>`;
}

function cookiebotConsentRenewal(config: Config) {
	if (config.store !== 'kgno') return '';

	return `<script>
	(function () {
		var oldConsentExpired = false;
		var cutoffDate = new Date('2026-04-15');
		var expiredCookie = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';

		function getCookieValue(name) {
			var prefix = name + '=';
			var cookies = document.cookie.split(';');

			for (var i = 0; i < cookies.length; i++) {
				var cookie = cookies[i].trim();
				if (cookie.indexOf(prefix) === 0) {
					return cookie.substring(prefix.length);
				}
			}
		}

		function getConsentCookieDate() {
			var consentCookie = getCookieValue('CookieConsent');
			if (!consentCookie || consentCookie === '-1') return;

			var decodedConsentCookie;
			try {
				decodedConsentCookie = decodeURIComponent(consentCookie);
			} catch (error) {
				decodedConsentCookie = consentCookie;
			}

			var utcMatch = decodedConsentCookie.match(/(?:^|[,{])\\s*utc\\s*:\\s*'?([^,'}\\s]+)'?/i);
			if (!utcMatch) return;

			var utcValue = utcMatch[1];
			var timestamp = Number(utcValue);

			if (isFinite(timestamp)) {
				return new Date(timestamp < 100000000000 ? timestamp * 1000 : timestamp);
			}

			return new Date(utcValue);
		}

		function expireCookie(name) {
			var hostname = window.location.hostname;
			var rootDomain = hostname.split('.').slice(-2).join('.');

			document.cookie = name + expiredCookie;
			document.cookie = name + expiredCookie + '; domain=' + hostname;
			document.cookie = name + expiredCookie + '; domain=.' + rootDomain;
			document.cookie = name + expiredCookie + '; domain=' + rootDomain;
		}

		function expireOldCookiebotConsent() {
			var consentDate = getConsentCookieDate();

			if (consentDate && !isNaN(consentDate.getTime()) && consentDate < cutoffDate) {
				oldConsentExpired = true;
				expireCookie('CookieConsent');
			}
		}

		function showCookiebotDialogForExpiredConsent() {
			if (
				!oldConsentExpired ||
				!window.Cookiebot ||
				typeof window.Cookiebot.renew !== 'function'
			) {
				return;
			}

			window.Cookiebot.renew();
			oldConsentExpired = false;
		}

		expireOldCookiebotConsent();
		window.addEventListener('CookiebotOnConsentReady', showCookiebotDialogForExpiredConsent);
		window.addEventListener('CookiebotOnLoad', showCookiebotDialogForExpiredConsent);
		showCookiebotDialogForExpiredConsent();
	})();
</script>`;
}

export function htmlTemplate(
	template: string,
	{
		helmet,
		preloadedData,
		markup,
		config,
		localization,
		isPreview = false,
	}: {
		helmet?: HelmetServerState;
		markup: string;
		preloadedData: unknown;
		config: Config;
		localization: unknown;
		isPreview?: boolean;
	}
) {
	function helmetValue(key: keyof HelmetServerState) {
		return helmet?.[key].toString() ?? '';
	}

	// A draft preview is a real page load on the live domain: without this it would show up in
	// the analytics of whatever page it is previewing, and the draft id would leak to every
	// third party the page talks to through the referrer. Sentry stays on - an error in a draft
	// is exactly the thing worth catching before it is published.
	const whenNotPreview = (tracker: string) => (isPreview ? '' : tracker);
	const previewMeta = isPreview
		? '<meta name="referrer" content="no-referrer"><meta name="robots" content="noindex, nofollow">'
		: '';

	return template
		.replace('<!-- helmet_title -->', previewMeta + helmetValue('title'))
		.replace('<!-- helmet_meta -->', helmetValue('meta'))
		.replace('<!-- helmet_link -->', helmetValue('link'))
		.replace('<!-- lang -->', config.lang.toLowerCase())
		.replace('<!-- favicon -->', config.favicon)
		.replace('<!-- helmet_script -->', helmetValue('script'))
		.replace('<!-- googleTagManager -->', whenNotPreview(getGoogleTagManager(config)))
		.replace('<!-- torkConversionLinker -->', whenNotPreview(torkConversionLinker(config)))
		.replace('<!-- torkFacebookTracking -->', whenNotPreview(torkFacebookTracking(config)))
		.replace('<!-- cookiebotConsentRenewal -->', cookiebotConsentRenewal(config))
		.replace('<!-- reCaptchaPubKey -->', config.reCaptchaPubKey)
		.replace(
			'<!-- chat -->',
			!config.chatProvider
				? ''
				: config.chatProvider === 'kundo'
					? config.inhouseChatStartHour !== undefined && config.inhouseChatEndHour !== undefined
						? timeBasedWidgetExcept(
								kundoChat(localization),
								config.inhouseChatStartHour,
								config.inhouseChatEndHour
							)
						: kundoChat(localization)
					: config.chatProvider === 'snapEngage'
						? snapEngage(config)
						: ''
		)
		.replace(
			'<!-- inhousechat -->',
			config.inhouseChatStartHour !== undefined && config.inhouseChatEndHour !== undefined
				? config.inhouseChatStartHour === config.inhouseChatEndHour
					? inhouseChat(config)
					: timeBasedWidget(inhouseChat(config), config.inhouseChatStartHour, config.inhouseChatEndHour)
				: ''
		)
		.replace('<!-- gtagHead -->', whenNotPreview(gtagHead(config)))
		.replace('<!-- gtagBody -->', whenNotPreview(gtagBody(config)))
		.replace('<!-- plausibleTracking -->', whenNotPreview(plausibleTracking(config)))
		.replace('<!-- markup -->', markup)
		.replace('<!-- config -->', jsValue(config))
		.replace('<!-- localization -->', jsValue(localization))
		.replace('<!-- preloadedData -->', jsValue(preloadedData));
}
